// 공통 골격(SCREENS §0): compact 하단 탭 5개 · medium/expanded 레일(expanded 는 펼침) · large 사이드바
import { useEffect, useRef, useState, type ReactNode } from "react";
import { clearSession } from "../auth";
import { currentMe } from "../data/actions";
import { stats } from "../data/select";
import { stopEngine, syncNow, useView } from "../data/store";
import { fmtDateLong, todayStr } from "../lib/date";
import { applyTheme, readTheme, THEME_KEY, type ThemeMode } from "../theme";
import { go, useLowHeight, usePref, useWidthClass, type Route } from "./hooks";
import { Icon } from "./icons";

type NavItem = { key: string; path: string; label: string; short: string; icon: string; adminOnly?: boolean };
const NAV: NavItem[] = [
  { key: "", path: "", label: "대시보드", short: "홈", icon: "home" },
  { key: "today", path: "today", label: "당일 클리닉", short: "당일", icon: "today" },
  { key: "week", path: "week", label: "주간 일정표", short: "주간", icon: "week" },
  { key: "students", path: "students", label: "학생", short: "학생", icon: "students" },
  { key: "board", path: "board", label: "현황·경고카드", short: "경고", icon: "board" },
  { key: "admin", path: "admin", label: "관리", short: "관리", icon: "admin", adminOnly: true },
];
export const TITLES: Record<string, string> = {
  "": "대시보드", today: "당일 클리닉", week: "주간 일정표", students: "학생", board: "현황·경고카드", admin: "관리", register: "개별일정 등록",
};

export function Shell({ route, children }: { route: Route; children: ReactNode }) {
  const wc = useWidthClass();
  const low = useLowHeight();
  const v = useView();
  const me = currentMe();
  const [railOpen, setRailOpen] = usePref<"open" | "closed">("centium.rail", "closed");
  const active = route.name;
  const items = NAV.filter((n) => !n.adminOnly || me?.admin);
  const title = TITLES[active] ?? "센티움 클리닉";
  const todayN = stats(v, todayStr()).total;
  const th = v.settings.warnThreshold;
  const heavy = v.students.filter((s) => v.warns.filter((w) => w.studentId === s.id).length >= th).length;

  // 단축키 N = 개별일정 등록(입력 중이 아닐 때)
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key.toLowerCase() !== "n" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && (t.closest("input, textarea, select, [contenteditable=true]"))) return;
      go("register");
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);

  useEffect(() => { document.title = `${title} · 센티움 클리닉`; }, [title]);

  const navMode = wc === "compact" ? "bottom" : wc === "large" ? "side" : railOpen === "open" && wc === "expanded" ? "drawer" : "rail";
  const showFab = active !== "register" && wc !== "large";

  return (
    <div className={`app mode-${navMode} wc-${wc}`}>
      {navMode === "bottom" ? (
        <nav className="nav-bottom" aria-label="주 메뉴">
          {items.filter((n) => n.key !== "admin").map((n) => (
            <a key={n.key} href={"#/" + n.path} className={"nb-item" + (active === n.key ? " on" : "")} aria-current={active === n.key ? "page" : undefined}>
              <Icon name={n.icon} />
              <span>{n.short}</span>
            </a>
          ))}
        </nav>
      ) : navMode === "side" ? (
        <nav className="nav-side" aria-label="주 메뉴">
          <a href="#/" className="side-logo" aria-label="대시보드"><Logo full /></a>
          <a href="#/register" className="btn btn-primary side-cta"><Icon name="plus" size={20} /> 개별일정 등록 <kbd>N</kbd></a>
          {items.map((n) => (
            <a key={n.key} href={"#/" + n.path} className={"side-item" + (active === n.key ? " on" : "")} aria-current={active === n.key ? "page" : undefined}>
              <Icon name={n.icon} />
              <span className="grow">{n.label}</span>
              {n.key === "today" && todayN ? <span className="badge">{todayN}</span> : null}
              {n.key === "board" && heavy ? <span className="badge badge-warn">{heavy}</span> : null}
            </a>
          ))}
        </nav>
      ) : (
        <nav className={"nav-rail" + (navMode === "drawer" ? " open" : "")} aria-label="주 메뉴">
          {wc === "expanded" ? (
            <button type="button" className="rail-toggle" aria-label={navMode === "drawer" ? "메뉴 접기" : "메뉴 펼치기"} aria-expanded={navMode === "drawer"}
              onClick={() => setRailOpen(navMode === "drawer" ? "closed" : "open")}>
              <Icon name={navMode === "drawer" ? "left" : "menu"} />
            </button>
          ) : null}
          <a href="#/register" className="rail-cta" aria-label="개별일정 등록"><Icon name="plus" /><span>등록</span></a>
          {items.map((n) => (
            <a key={n.key} href={"#/" + n.path} className={"rail-item" + (active === n.key ? " on" : "")} aria-current={active === n.key ? "page" : undefined}>
              <span className="rail-ic"><Icon name={n.icon} /></span>
              <span className="rail-l">{navMode === "drawer" ? n.label : n.short}</span>
            </a>
          ))}
        </nav>
      )}

      <div className="frame">
        <header className="topbar">
          {wc === "compact" ? <Logo /> : wc !== "large" ? <Logo full /> : null}
          <h1 className="topbar-title">
            {wc === "compact" ? title : <><span className="muted">클리닉 · </span>{title}</>}
          </h1>
          <span className="grow" />
          {wc === "large" ? <span className="topbar-date">{fmtDateLong(todayStr())}</span> : null}
          <SyncBadge />
          <Profile compact={wc === "compact"} />
        </header>
        <main className="main" id="main">{children}</main>
      </div>

      {showFab ? (
        <a href="#/register" className={"fab" + (wc === "compact" && low ? " fab-icon" : "")} aria-label="개별일정 등록">
          <Icon name="plus" /><span className="fab-l">개별일정 등록</span>
        </a>
      ) : null}
    </div>
  );
}

export function Logo({ full }: { full?: boolean }) {
  const n = full ? "logo_full" : "logo_symbol";
  return (
    <span className={"logo" + (full ? " logo-full" : "")}>
      <img className="only-light" src={`brand/${n}.png`} alt="센티움" />
      <img className="only-dark" src={`brand/${n}_dark.png`} alt="센티움" />
    </span>
  );
}

function SyncBadge() {
  const { status } = useView();
  const off = !status.online || !!status.error;
  const label = off ? (status.pending ? `오프라인 · 대기 ${status.pending}` : "오프라인") : status.pending ? `저장 중 ${status.pending}` : "동기화됨";
  return (
    <button type="button" className={"sync" + (off ? " sync-off" : status.pending ? " sync-busy" : "")} onClick={() => void syncNow()}
      title={status.lastSync ? `마지막 동기화 ${new Date(status.lastSync).toLocaleTimeString("ko-KR")}` : "아직 동기화 전"} aria-label={label}>
      <Icon name="cloud" size={18} />
      <span className="sync-l">{label}</span>
    </button>
  );
}

function Profile({ compact }: { compact: boolean }) {
  const me = currentMe();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ThemeMode>(readTheme);
  const ref = useRef<HTMLDivElement>(null);
  const { status } = useView();
  useEffect(() => {
    if (!open) return;
    const on = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", on);
    return () => document.removeEventListener("mousedown", on);
  }, [open]);
  const pick = (m: ThemeMode) => {
    setMode(m);
    try { localStorage.setItem(THEME_KEY, m); } catch { /* 무시 */ }
    applyTheme(m);
  };
  const logout = () => {
    if (status.pending && !confirm(`아직 서버에 못 보낸 변경 ${status.pending}건이 있습니다. 로그아웃하면 사라집니다. 계속할까요?`)) return;
    stopEngine(true);
    clearSession();
    go("login");
  };
  return (
    <div className="profile" ref={ref}>
      <button type="button" className="avatar" aria-haspopup="menu" aria-expanded={open} aria-label={`${me?.name || "내 계정"} 메뉴`} onClick={() => setOpen(!open)}>
        {(me?.name || "·").slice(0, 1)}
      </button>
      {open ? (
        <div className="menu" role="menu">
          <p className="menu-who"><b>{me?.name || "직원"}</b>{me?.admin ? " · 관리자" : ""}</p>
          <p className="menu-label">화면</p>
          <div className="seg" role="group" aria-label="화면 테마">
            {(["light", "dark", "system"] as ThemeMode[]).map((m) => (
              <button key={m} type="button" className={mode === m ? "on" : ""} aria-pressed={mode === m} onClick={() => pick(m)}>
                {m === "light" ? "밝게" : m === "dark" ? "어둡게" : "시스템"}
              </button>
            ))}
          </div>
          {compact && me?.admin ? <a className="menu-item" href="#/admin" onClick={() => setOpen(false)}><Icon name="admin" size={20} /> 관리</a> : null}
          <button type="button" className="menu-item" onClick={logout}><Icon name="logout" size={20} /> 로그아웃</button>
        </div>
      ) : null}
    </div>
  );
}
